/**
 * Announcements list page.
 * Shows all announcements with links to view individual ones.
 * GMs see a "Create Announcement" button.
 */

import { Head, Link, usePage } from '@inertiajs/react';
import { EditorRenderer } from '@/components/Editor/EditorRenderer';

interface Announcement {
    id: number;
    title: string;
    content: { blocks: Array<{ type: string; data: Record<string, unknown> }> };
    scope: string;
    created_at: string;
    author: {
        id: number;
        username: string;
    };
}

interface Props {
    announcements: {
        data: Announcement[];
        current_page: number;
        last_page: number;
        next_page_url: string | null;
        prev_page_url: string | null;
    };
}

export default function Index({ announcements }: Props) {
    const { auth } = usePage().props as { auth: { user: { role: string } } };
    const isGm = auth?.user?.role === 'gm';

    return (
        <>
            <Head title="Announcements" />
            <div className="min-h-screen bg-slate-900 text-white">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <Link
                                href="/map"
                                className="text-slate-300 hover:text-white transition-colors"
                            >
                                ← Back to Map
                            </Link>
                            <h1 className="text-3xl font-bold">Announcements</h1>
                        </div>
                        {isGm && (
                            <Link
                                href="/gm/announcements/create"
                                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded-lg font-medium transition-colors"
                            >
                                + New Announcement
                            </Link>
                        )}
                    </div>

                    {/* Announcements List */}
                    {announcements.data.length === 0 ? (
                        <div className="text-center py-12 text-slate-300">
                            <p>No announcements yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {announcements.data.map((announcement) => (
                                <article
                                    key={announcement.id}
                                    className="bg-slate-800 rounded-lg p-6 border border-slate-700"
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <Link
                                                href={`/announcements/${announcement.id}`}
                                                className="text-xl font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                                            >
                                                {announcement.title}
                                            </Link>
                                            <p className="text-sm text-slate-300 mt-1">
                                                By {announcement.author.username} •{' '}
                                                {new Date(announcement.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                        {isGm && (
                                            <Link
                                                href={`/gm/announcements/${announcement.id}/edit`}
                                                className="text-sm text-slate-300 hover:text-white transition-colors"
                                            >
                                                Edit
                                            </Link>
                                        )}
                                    </div>
                                    <div className="prose prose-invert prose-sm max-w-none">
                                        <EditorRenderer content={announcement.content} />
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {announcements.last_page > 1 && (
                        <div className="flex justify-center gap-4 mt-8">
                            {announcements.prev_page_url && (
                                <Link
                                    href={announcements.prev_page_url}
                                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                                >
                                    ← Previous
                                </Link>
                            )}
                            <span className="px-4 py-2 text-slate-300">
                                Page {announcements.current_page} of {announcements.last_page}
                            </span>
                            {announcements.next_page_url && (
                                <Link
                                    href={announcements.next_page_url}
                                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                                >
                                    Next →
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

